import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCalendlyEventListener } from 'react-calendly'
import { Grid, TextInput } from '@percent/lemonade'
import {
  useAuthState,
  useMarketplaceAnalytics,
  useMutation,
  useNotificationBar,
  useSitewideMessage
} from '@percent/cause-dashboard/common/hooks'
import {
  canUserBookCall,
  isOfferAllowedForClaimState,
  isOfferAllowedInCountry,
  shouldDisplayOffer
} from '@percent/cause-dashboard/common/utility/productOffers/productOffers'
import type { IProductCategory } from '@percent/cause-dashboard/app/productOffers/data/categories'
import { BannersCarousel } from '@percent/cause-dashboard/app/productOffers/bannersCarousel/BannersCarousel'
import type { MarketplaceBannerCarouselItem } from '@percent/cause-dashboard/storyblok/product-offers/banners/mapStoryblokBannerSlideToBannerProps'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useBreadcrumbs } from '../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { AppStoreCard } from './AppStoreCard/AppStoreCard'
import styles from './ProductOffersPage.module.scss'
import { ProductOffer } from './data/data.types'
import { EmptyState } from '../common/components/emptyState/EmptyState'

type Categorized = Array<{ id: number } & IProductCategory>

export type ProductOffersPageContentProps = {
  categorizedProducts: Categorized
  /** From Storyblok `banners_carousel` in `page.body`; omit or pass `[]` to hide the carousel. */
  bannerCarouselSlides?: MarketplaceBannerCarouselItem[]
}

export function ProductOffersPageContent({
  categorizedProducts,
  bannerCarouselSlides = []
}: Readonly<ProductOffersPageContentProps>) {
  const { t } = useTranslation()
  useBreadcrumbs([{ label: t('breadcrumb.productOffers.title') }])

  const { trackMarketplaceSearch, trackMarketplaceSearchPillClick } = useMarketplaceAnalytics()
  const { calendlyService } = useServices()
  const { authState } = useAuthState()
  const notification = useNotificationBar()
  const { shouldShowSitewideMessage } = useSitewideMessage()

  const suggestedSearchTerms = [
    'Grants',
    'Adobe',
    'Fundraising',
    ...(authState.organisation ? ['Expert Services'] : []),
    'Email',
    'Free'
  ]

  const [{ isLoading }, { apiFunc: saveMeetingLink }] = useMutation(calendlyService.saveMeetingLink)

  const [selectedOfferName, setSelectedOfferName] = useState<string | undefined>(undefined)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const trackedSearchesRef = useRef<Set<string>>(new Set())

  const canBookCallResult = canUserBookCall(authState)

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchQuery.trim() && !trackedSearchesRef.current.has(searchQuery.trim())) {
      searchTimeoutRef.current = setTimeout(() => {
        trackMarketplaceSearch({
          searchQuery: searchQuery.trim()
        })
        trackedSearchesRef.current.add(searchQuery.trim())
      }, 1000)
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery, trackMarketplaceSearch])

  useCalendlyEventListener({
    onEventScheduled: event => {
      const uri = event.data.payload.event.uri
      if (uri && !isLoading) {
        saveMeetingLink({
          accountId: authState.user?.id,
          organisationId: authState.user?.organisationId ?? '',
          email: authState.user?.email,
          source: selectedOfferName ?? 'app-store-offer',
          meetingLink: uri
        })
      }
      setSelectedOfferName(undefined)
    }
  })

  const defaultTop = 70
  const shiftDownToAccommodateNotification = notification.isOpened || shouldShowSitewideMessage ? 48 : 0
  const topPosition = defaultTop + shiftDownToAccommodateNotification

  const filterOffersBySearch = (offer: ProductOffer, categoryName: string) => {
    if (!searchQuery.trim()) return true

    const query = searchQuery.toLowerCase()

    const searchableText = [offer.name, offer.description, offer.tracking, categoryName, ...(offer.searchTags || [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    const queryWords = query.split(/\s+/).filter(word => word.length > 0)

    return queryWords.every(word => searchableText.includes(word))
  }

  const filteredCategories = useMemo(() => {
    const seenOfferNames = searchQuery.trim() ? new Set<string>() : null

    return categorizedProducts
      .map(category => ({
        ...category,
        offers: category.offers.filter(offer => {
          if (seenOfferNames && seenOfferNames.has(offer.name)) {
            return false
          }

          const passesFilters =
            isOfferAllowedForClaimState(offer, authState) &&
            shouldDisplayOffer(offer, canBookCallResult, isOfferAllowedInCountry(authState, offer.allowedCountries)) &&
            filterOffersBySearch(offer, category.name)

          if (passesFilters && seenOfferNames) {
            seenOfferNames.add(offer.name)
          }

          return passesFilters
        })
      }))
      .filter(category => category.offers.length > 0)
  }, [categorizedProducts, authState, canBookCallResult, searchQuery])

  const totalResults = useMemo(() => {
    return filteredCategories.reduce((sum, category) => sum + category.offers.length, 0)
  }, [filteredCategories])

  const handleSuggestedTermClick = (term: string) => {
    trackMarketplaceSearchPillClick({ searchQuery: term })
    trackedSearchesRef.current.add(term)
    setSearchQuery(term)
  }

  return (
    <div id="marketplace-container">
      <div className={styles.productCategoriesWrapper}>
        <section key="searchbox-results" className={styles.productOffersWrapper} data-testid="searchbox-results">
          <div className={styles.sectionHeader} style={{ top: topPosition }}>
            <div className={styles.sectionTitle}>What are you looking for?</div>
            <div className={styles.searchboxWrapper}>
              <TextInput
                placeholder="Search for tools or key words"
                onChange={e => setSearchQuery(e.target.value)}
                value={searchQuery}
                name="search-box"
                icon="search"
              />
            </div>
            <div className={styles.suggestedTerms}>
              {suggestedSearchTerms.map(term => (
                <button
                  key={term}
                  className={styles.suggestedTermBubble}
                  onClick={() => handleSuggestedTermClick(term)}
                  type="button"
                >
                  {term}
                </button>
              ))}
            </div>
            {searchQuery && (
              <>
                <div className={styles.searchResultsCount}>
                  {totalResults} {totalResults === 1 ? 'result' : 'results'} found
                </div>
                {totalResults === 0 && (
                  <EmptyState illustration="no-search-results" title="We couldn't find what you're looking for" />
                )}
              </>
            )}
            {!searchQuery && bannerCarouselSlides.length > 0 && <BannersCarousel slides={bannerCarouselSlides} />}
          </div>

          {searchQuery && (
            <div className={styles.searchResultsWrapper}>
              <Grid gutter={12} className={styles.productOffersPageGrid}>
                {filteredCategories
                  .flatMap(category =>
                    category.offers.map(productOffer => ({
                      ...productOffer,
                      categoryName: category.name,
                      categoryTracking: category.tracking
                    }))
                  )
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(productOffer => (
                    <Grid.Col span={{ sm: 12, md: 6, lg: 4 }} key={productOffer.name}>
                      <AppStoreCard
                        {...productOffer}
                        category={productOffer.categoryName}
                        selectOffer={setSelectedOfferName}
                        selectedOfferName={selectedOfferName}
                        searchQuery={searchQuery}
                        tracking={{
                          name: productOffer.tracking,
                          category: productOffer.categoryTracking
                        }}
                      />
                    </Grid.Col>
                  ))}
              </Grid>
            </div>
          )}
        </section>

        {!searchQuery &&
          filteredCategories.map(category => (
            <section
              key={category.id}
              title={category.name}
              className={styles.productOffersWrapper}
              data-testid={category.name}
            >
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTitle}>{category.name}</div>
                <div className={styles.sectionDescription}>{category.description}</div>
              </div>
              <Grid gutter={12} className={styles.productOffersPageGrid}>
                {category.offers.map(productOffer => (
                  <Grid.Col span={{ sm: 12, md: 6, lg: 4 }} key={productOffer.name}>
                    <AppStoreCard
                      {...productOffer}
                      category={category.name}
                      selectOffer={setSelectedOfferName}
                      selectedOfferName={selectedOfferName}
                      tracking={{
                        name: productOffer.tracking,
                        category: category.tracking
                      }}
                    />
                  </Grid.Col>
                ))}
              </Grid>
            </section>
          ))}
      </div>
    </div>
  )
}
